// @flow

import React from 'react';
import type { Node } from 'react';
import styled, { css } from 'styled-components';
import { Box } from '../grid';
import type { ColumnImage } from './types';
import type {
  ACFRowTemplate,
  ACFCut,
  ACFColumnColor,
  ACFVerticalAlign,
  Theme,
} from '../../types';
import {
  getColumnStyling,
  getColumnSpread,
  alignmentMapper,
} from './helpers';
import Divider from '../divider';
import {
  getBackgroundColor,
  getColor,
  breakpointsToArray,
} from '../../helpers';
import type { BreakpointData } from '../row/types';
import DimensionContext from '../../context/dimensionContext';

type ColumnBoxProps = {
  className?: string, // eslint-disable-line react/require-default-props
  children: any,
};

export const ColumnBox = ({
  className,
  children,
}: ColumnBoxProps) => (
  <DimensionContext.Consumer>
    {dimensions => (
      <Box
        className={className}
        px={3}
        // $FlowFixMe
        width={breakpointsToArray(dimensions)}
      >
        {children}
      </Box>
    )}
  </DimensionContext.Consumer>
);

export type Props = {
  rowTemplate?: ACFRowTemplate, // eslint-disable-line react/require-default-props
  cut?: ACFCut, // eslint-disable-line react/require-default-props
  backgroundColor?: ACFColumnColor, // eslint-disable-line react/require-default-props
  verticalAlign?: ACFVerticalAlign, // eslint-disable-line react/require-default-props
  responsiveData: BreakpointData,
  image: ?ColumnImage,
  slideshow?: ?boolean, // eslint-disable-line react/require-default-props
  componentsOverCut: boolean,
  className?: string, // eslint-disable-line react/require-default-props
  slidersEnabled: boolean,
  children: Node,
  autoMargin?: boolean, // eslint-disable-line react/require-default-props
};

// These are 'defaulted' by .attrs()
type StyledProps = {
  ...Props,
  rowTemplate: ACFRowTemplate, // eslint-disable-line react/no-unused-prop-types
  cut: ACFCut, // eslint-disable-line react/no-unused-prop-types
  backgroundColor: ACFColumnColor, // eslint-disable-line react/no-unused-prop-types
  verticalAlign: ACFVerticalAlign, // eslint-disable-line react/no-unused-prop-types
  slideshow: boolean,
  autoMargin?: boolean,
};

const Column = styled<typeof ColumnBox, Theme, StyledProps>(ColumnBox)`
  box-sizing: border-box;
  display: flex;
  position: relative;
  flex-direction: column;

  ${props => props.autoMargin && css`
    /* stylelint-disable no-descending-specificity */
    > * {
      margin-bottom: ${props.theme.column.padding};
    }
    /* stylelint-enable no-descending-specificity */
  `}

  ${props => props.verticalAlign !== 'top' && css`
    justify-content: ${alignmentMapper[props.verticalAlign]};
    flex-wrap: wrap;
    /* stylelint-disable no-descending-specificity */
    > * {
      width: 100%;
    }
    /* stylelint-enable no-descending-specificity */
  `}

  > *:last-child {
    margin-bottom: 0;
  }

  /* hr elements within flex parents won't show unless width: 100% */
  > ${/* sc-sel */Divider} {
    width: 100%;
  }

  ${props => getColumnStyling(props.rowTemplate, props.cut, props.responsiveData, props.image, props.componentsOverCut, props.theme)}
  ${props => getBackgroundColor(props.backgroundColor, props.theme, 'transparent')}
  ${props => getColor(props.backgroundColor, props.theme, 'transparent') !== 'transparent' && getColumnSpread(props.responsiveData)}
  ${props => props.slidersEnabled && props.slideshow && css`
    width: 100%;
  `}
  ${props => props.componentsOverCut && css`
    z-index: 1;
  `}
`;

const StyledColumn = (props: Props) => {
  const {
    backgroundColor = 'default',
    cut = 'default',
    rowTemplate = 'default',
    verticalAlign = 'top',
    slideshow = false,
    autoMargin = true,
    children,
    ...rest
  } = props;
  // eslint-disable-next-line react/destructuring-assignment
  return (
    <Column
    // $FlowFixMe
      {...rest}
      backgroundColor={backgroundColor}
      cut={cut}
      rowTemplate={rowTemplate}
      verticalAlign={verticalAlign}
      slideshow={slideshow}
      autoMargin={autoMargin}
    >
      {children}
    </Column>
  );
};

StyledColumn.defaultProps = {
  backgroundColor: 'default',
  cut: 'default',
  rowTemplate: 'default',
};

export default StyledColumn;
